/**
 * Enemy barks: non-verbal vocal blips, one per `BarkKind`, each with its own contour so the
 * player learns them like birdsong. Rising = a question ("huh?"), sharp and loud = contact,
 * falling and slow = standing down. Each soldier plays them at his own pitch (see `barkRate`).
 */

import type { BarkKind } from '../../sim/types';
import { DOG_BARK } from './dogs';
import { PRIORITY, type SfxDef } from './types';
import { type Syllable, utterance } from './voice';

const bark = (syllables: Syllable[], gain = 0.45): SfxDef => ({
  patch: { layers: utterance(syllables), jitter: { pitch: 0.03, time: 0.06 }, drive: 1.2 },
  variants: 2, priority: PRIORITY.cue, gain, maxDistance: 70,
});

export const BARK_SFX = {
  'bark.contact': bark([
    { at: 0, dur: 0.09, from: 250, to: 270, vowel: 'ah', attack: 0.005 },
    { at: 0.11, dur: 0.2, from: 310, to: 190, vowel: 'ah', attack: 0.005, gain: 1.2 },
  ], 0.55),
  'bark.flanking': bark([
    { at: 0, dur: 0.12, from: 170, to: 210, vowel: 'eh' },
    { at: 0.15, dur: 0.22, from: 232, to: 240, vowel: 'ee' },
  ]),
  'bark.covering': bark([
    { at: 0, dur: 0.13, from: 150, to: 145, vowel: 'uh' },
    { at: 0.2, dur: 0.13, from: 150, to: 140, vowel: 'uh' },
  ]),
  'bark.grenade': bark([
    { at: 0, dur: 0.07, from: 260, to: 280, vowel: 'ah', attack: 0.004 },
    { at: 0.09, dur: 0.07, from: 300, to: 320, vowel: 'ah', attack: 0.004 },
    { at: 0.18, dur: 0.24, from: 370, to: 300, vowel: 'ah', attack: 0.004, vib: 0.4, gain: 1.2 },
  ], 0.55),
  'bark.manDown': bark([
    { at: 0, dur: 0.12, from: 280, to: 300, vowel: 'oh' },
    { at: 0.14, dur: 0.5, from: 300, to: 150, vowel: 'oh', vib: 0.5 },
  ]),
  'bark.alarm': bark([
    { at: 0, dur: 0.6, from: 260, to: 380, vowel: 'eh', attack: 0.03, vib: 0.6 },
    { at: 0.65, dur: 0.4, from: 380, to: 360, vowel: 'ee', vib: 0.6 },
  ], 0.6),
  'bark.help': bark([
    { at: 0, dur: 0.15, from: 220, to: 260, vowel: 'eh' },
    { at: 0.2, dur: 0.42, from: 260, to: 365, vowel: 'eh', vib: 0.5 },
  ], 0.5),
  // The "huh?": one slow rising syllable. If you hear this, someone is about to come and look.
  'bark.searching': bark([{ at: 0, dur: 0.3, from: 138, to: 235, vowel: 'uh', attack: 0.03 }]),
  'bark.allClear': bark([
    { at: 0, dur: 0.2, from: 210, to: 200, vowel: 'oh' },
    { at: 0.26, dur: 0.34, from: 165, to: 122, vowel: 'oh' },
  ], 0.4),
  'bark.suppressed': bark([{ at: 0, dur: 0.17, from: 165, to: 100, vowel: 'uh', breath: 0.5 }], 0.38),
  // ROUND8: a dog's one sharp woof (dogs.ts) - at bay the engine plays a double instead (dogVoice.ts).
  'bark.dogBark': DOG_BARK,
} satisfies Record<`bark.${BarkKind}`, SfxDef>;

/** Bark sound per kind; a new `BarkKind` fails to compile until it has a voice. */
export const BARK_ID: Record<BarkKind, keyof typeof BARK_SFX> = {
  contact: 'bark.contact',
  dogBark: 'bark.dogBark',
  flanking: 'bark.flanking',
  covering: 'bark.covering',
  grenade: 'bark.grenade',
  manDown: 'bark.manDown',
  alarm: 'bark.alarm',
  help: 'bark.help',
  searching: 'bark.searching',
  allClear: 'bark.allClear',
  suppressed: 'bark.suppressed',
};

/** Each soldier has his own voice: a playback rate from his id, 0.88 .. 1.14. */
export function barkRate(enemyId: number): number {
  return 0.88 + (((Math.imul(enemyId, 0x9e3779b1) >>> 0) % 1000) / 1000) * 0.26;
}
